import { UsedOutsideProviderError } from '@diritto/common/utils/exceptions';
import { createContext, ReactNode, useContext, useMemo } from 'react';
import { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query';
import { accurateItems, flatItems, toMapByKey } from '@diritto/common/utils/array';
import { ProfilePublicRelayDto } from '@diritto/common/relay/dtos/profile-relay.dtos';
import { ContentPublicRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import { RollingDispatchState, useRollingDispatch } from 'state-hooks/rolling/useRollingDispatch';
import { RollingRelayDto, RollingsRelayResponseDto } from '@diritto/common/relay/dtos/rolling-relay.dtos';
import { RollingReactionDto } from '@diritto/common/rolling/dtos/rolling-reaction.dto';
import { usePostRollingReactionsQuery } from 'queries/rollingReaction/usePostRollingReactionsQuery';

interface RollingState {
  isPending: boolean;
  rollings: RollingRelayDto[];
  hasNextPage: boolean;
  fetchNextPage: () => Promise<any>;
  profiles: Record<number, ProfilePublicRelayDto>;
  contents: Record<number, ContentPublicRelayDto>;
  rollingReactions: Record<number, RollingReactionDto>;
}

const RollingStateContext = createContext<RollingState>({
  isPending: true,
  rollings: [],
  hasNextPage: false,
  fetchNextPage: () => Promise.resolve(undefined),
  profiles: {},
  contents: {},
  rollingReactions: {},
});

const RollingDispatchContext = createContext<RollingDispatchState>({
  dispatchAction(): Promise<void> {
    return Promise.resolve(undefined);
  },
  rolling: undefined,
  isPending: false,
  isInputFocus: false,
});

export const RollingProvider = ({
  children,
  rollingsQuery,
}: {
  children: ReactNode;
  rollingsQuery: UseInfiniteQueryResult<InfiniteData<RollingsRelayResponseDto>, unknown>;
}) => {
  const { rollings, contents, profiles } = useMemo(
    () => ({
      rollings: flatItems<RollingRelayDto>('rollings', rollingsQuery.data?.pages) ?? [],
      contents: accurateItems<ContentPublicRelayDto>('contents', rollingsQuery.data?.pages) ?? {},
      profiles: accurateItems<ProfilePublicRelayDto>('profiles', rollingsQuery.data?.pages) ?? {},
    }),
    [rollingsQuery.data?.pages],
  );

  const rollingIds = useMemo(() => rollings.map(rolling => rolling.id), [rollings]);

  const { data: reactionData } = usePostRollingReactionsQuery(rollingIds);

  const state = useMemo<RollingState>(
    () => ({
      contents,
      profiles,
      isPending: rollingsQuery.isPending,
      hasNextPage: !!rollingsQuery.hasNextPage,
      fetchNextPage: rollingsQuery.fetchNextPage,
      rollings,
      rollingReactions: toMapByKey('rollingId', reactionData?.rollingReactions ?? []),
    }),
    [
      contents,
      profiles,
      reactionData?.rollingReactions,
      rollings,
      rollingsQuery.fetchNextPage,
      rollingsQuery.hasNextPage,
      rollingsQuery.isPending,
    ],
  );

  return (
    <RollingStateContext.Provider value={state}>
      <RollingDispatchContext.Provider value={useRollingDispatch(rollingsQuery.refetch)}>
        {children}
      </RollingDispatchContext.Provider>
    </RollingStateContext.Provider>
  );
};

export const useRollingState = () => {
  const context = useContext(RollingStateContext);

  if (context === undefined) {
    throw new UsedOutsideProviderError('useRolling State was used outside of its Provider');
  }
  return context;
};

export const useRollingDispatchState = () => {
  const context = useContext(RollingDispatchContext);

  if (context === undefined) {
    throw new UsedOutsideProviderError('useRolling State was used outside of its Provider');
  }
  return context;
};
